/**
 * AR 장면(A-Frame + MindAR)을 세우는 규칙.
 *
 * v1(`ai-music-video`)의 7/29 QA 미해결 4건이 전부 이 파일이 답해야 하는 것들이라,
 * «왜 이렇게 하는가» 를 사양(spec)으로 떼어내 검사로 박는다.
 */

/** 엔진은 CDN 스크립트로 온다. npm 설치 경로는 node-canvas 네이티브 빌드에서 막힌다. */
export const AFRAME_URL = "https://aframe.io/releases/1.5.0/aframe.min.js";
export const MINDAR_AFRAME_URL =
  "https://cdn.jsdelivr.net/npm/mind-ar@1.2.5/dist/mindar-image-aframe.prod.js";

export type ScriptLoader = (src: string) => Promise<void>;

export function loadScript(src: string): Promise<void> {
  return new Promise((resolve, reject) => {
    if (document.querySelector(`script[src="${src}"]`)) {
      resolve();
      return;
    }
    const element = document.createElement("script");
    element.src = src;
    element.addEventListener("load", () => resolve());
    element.addEventListener("error", () => reject(new Error(`SCRIPT_FAILED:${src}`)));
    document.head.appendChild(element);
  });
}

let engineLoaded = false;

/** 🚨 A-Frame 이 먼저다 — MindAR 의 A-Frame 컴포넌트가 등록되려면 AFRAME 전역이 있어야 한다. */
export async function loadArEngine(load: ScriptLoader = loadScript): Promise<void> {
  if (engineLoaded) return;
  await load(AFRAME_URL);
  await load(MINDAR_AFRAME_URL);
  engineLoaded = true;
}

export interface SceneSpec {
  scene: Record<string, string>;
  video: Record<string, string>;
  videoProperties: { muted: boolean; loop: boolean; playsInline: boolean; crossOrigin: string | null };
}

export interface TrackingTuning {
  /** One Euro 필터의 저속 차단 주파수. 낮을수록 정지 상태 떨림이 줄고, 낮을수록 수렴이 느리다. */
  filterMinCF: number;
  /** 속도 반응 계수. 높을수록 움직임을 빨리 따라가고 그만큼 잡음도 통과시킨다. */
  filterBeta: number;
  /** 몇 프레임 연속으로 놓쳐야 targetLost 로 볼 것인가. */
  missTolerance: number;
  /** 몇 프레임 연속으로 봐야 targetFound 로 볼 것인가. */
  warmupTolerance: number;
}

/**
 * 키링 추적 기본값.
 *
 * 🔑 MindAR 기본값(minCF 0.001 / beta 1000)은 «움직이는 마커를 지연 없이 따라가기» 에
 *    맞춰져 있다. beta 1000 이면 미세한 손떨림도 속도로 간주해 필터가 사실상 꺼지고,
 *    그 잡음이 그대로 판의 떨림이 된다. 키링은 책상에 놓거나 손에 쥔 **정지 물체**라
 *    반응 속도를 크게 양보하고 떨림 억제를 사는 쪽이 맞다.
 *
 * 🚨 이 값은 합성 측정으로 고른 게 아니다 — 합성 장치는 떨림을 재지 못했다(2026-08-30,
 *    핸드오프 §-1-A). 실기기 비교용 쿼리 오버라이드(`?tune=`)를 두었으니, 실기기에서 더
 *    나은 값이 확인되면 여기를 그 값으로 바꾼다.
 *
 * missTolerance 기본(5)을 20 으로 올린 이유: 추적을 한두 프레임 놓칠 때마다 targetLost 가
 * 떠서 영상이 멎고 처음으로 되감긴다(ArScanPage). 이 «깜빡이며 재시작» 도 체감상 떨림이다.
 */
export const KEYRING_TRACKING: TrackingTuning = {
  filterMinCF: 0.0001,
  filterBeta: 10,
  missTolerance: 20,
  warmupTolerance: 5,
};

/**
 * 실기기 즉석 튜닝: `/k/<id>?tune=<minCF>,<beta>[,<miss>[,<warmup>]]`.
 * 값을 바꿔 볼 때마다 배포하지 않기 위한 문이다. 잘못된 입력은 조용히 기본값으로 —
 * 이 화면의 사용자는 아이라, 주소를 잘못 만졌다고 화면이 깨지면 안 된다.
 */
export function parseTrackingTuning(
  search: string,
  base: TrackingTuning = KEYRING_TRACKING,
): TrackingTuning {
  const raw = new URLSearchParams(search).get("tune");
  if (!raw) return base;
  const parts = raw.split(",").map((part) => Number(part));
  const pick = (index: number, fallback: number): number => {
    const value = parts[index] ?? Number.NaN;
    return Number.isFinite(value) && value > 0 ? value : fallback;
  };
  return {
    filterMinCF: pick(0, base.filterMinCF),
    filterBeta: pick(1, base.filterBeta),
    missTolerance: Math.round(pick(2, base.missTolerance)),
    warmupTolerance: Math.round(pick(3, base.warmupTolerance)),
  };
}

/**
 * 인식되면 화면 중앙에 «고정» 으로 뜨는 영상 오버레이의 스타일 — v1(우진학교) 방식.
 *
 * 🔑 왜 3D 판을 버렸나(2026-08-30 실기기 3회): 마커 재컴파일 → 필터 튜닝(PR #153) →
 *    렌더 자세 안정화(PR #154)까지 걸어도 떨림이 남았다. 40mm 마커의 프레임별 자세 추정
 *    잡음은 이 파이프라인으로 못 없앤다. v1 은 애초에 이 싸움을 하지 않았다 — MindAR 은
 *    «그림을 보고 있는가» 의 감지기로만 쓰고, 영상은 화면 중앙 고정 HTML `<video>` 로
 *    띄웠다(ai-music-video ArSamplePage). 고정이니 떨림이 구조적으로 0 이다.
 *
 * 🚨 이 결정으로 잃는 것: 영상이 그림 «위» 가 아니라 화면 중앙에 뜬다(그림에서 살짝
 *    떨어져도 그 자리). 키링 체험에서는 «비추면 내 영상이 나온다» 가 본질이고 v1 수업에서
 *    검증된 형태라 맞바꿀 가치가 있다.
 */
export const OVERLAY_VIDEO_STYLE: Readonly<Record<string, string>> = {
  position: "absolute",
  left: "50%",
  top: "50%",
  width: "72%",
  maxWidth: "560px",
  aspectRatio: "16 / 9",
  objectFit: "cover",
  transform: "translate(-50%, -50%)",
  borderRadius: "16px",
  boxShadow: "0 18px 45px rgba(0, 0, 0, 0.35)",
  zIndex: "3",
};

/**
 * 장면 사양.
 *
 * 🚨 **화면 가장자리에 뜨던 문제**(v1 7/29 QA: 「AR 오브젝트가 정면이 아니라 우상단」)는
 *    장면을 여백·둥근 모서리가 있는 카드 안에 넣어서 생긴다. MindAR 은 카메라 영상을
 *    **화면 전체 기준**으로 깔고 캔버스를 그 위에 맞추는데, 부모가 화면보다 작으면 둘이 어긋난다.
 *    그래서 이 장면은 항상 **전체 화면**에 붙인다(`mountFullScreen`).
 *
 * 🚨 **VR 버튼·ETag 같은 군더더기를 화면에 두지 않는다**(v1 7/29 QA 잔여 2건).
 *    A-Frame 은 기본으로 VR 진입 버튼을 그린다 — 아이가 누르면 빠져나올 길이 없다.
 *
 * 🔑 **소리는 꺼진 채로 시작한다.** 모바일 브라우저는 소리 있는 자동재생을 막고, 막히면
 *    영상이 아예 시작하지 않아 «빈 화면» 이 된다(v1 의 간헐 증상과 같은 계열).
 *    대신 화면이 「소리 켜기」를 내놓고, 사람이 누르면 그때 켠다.
 *
 * 🚨 `crossOrigin` 을 **설정하지 않는다.** 마커·영상은 같은 출처(서버 중계)로 오므로
 *    필요가 없고, 붙이면 오히려 요청이 CORS 검사를 타서 R2 직결 시절의 실패로 되돌아간다.
 */
export function buildSceneSpec(
  target: { markerUrl: string; videoUrl: string },
  tuning: TrackingTuning = KEYRING_TRACKING,
): SceneSpec {
  return {
    scene: {
      "mindar-image":
        `imageTargetSrc: ${target.markerUrl}; autoStart: true;`
        + " uiLoading: no; uiError: no; uiScanning: no;"
        + ` filterMinCF: ${tuning.filterMinCF}; filterBeta: ${tuning.filterBeta};`
        + ` missTolerance: ${tuning.missTolerance}; warmupTolerance: ${tuning.warmupTolerance};`,
      "color-space": "sRGB",
      renderer: "colorManagement: true, physicallyCorrectLights",
      "vr-mode-ui": "enabled: false",
      "device-orientation-permission-ui": "enabled: false",
      embedded: "",
    },
    video: {
      src: target.videoUrl,
      playsinline: "",
      "webkit-playsinline": "",
      preload: "auto",
    },
    videoProperties: { muted: true, loop: true, playsInline: true, crossOrigin: null },
  };
}

/** 카카오톡·인스타 같은 **앱 안 브라우저**는 카메라·WebGL 이 막히거나 반쪽이다. */
const IN_APP_PATTERN = /(KAKAOTALK|Instagram|FBAN|FBAV|NAVER\(inapp|DaumApps|Line\/)/iu;

export function isInAppBrowser(userAgent: string): boolean {
  return IN_APP_PATTERN.test(userAgent);
}

export function inAppBrowserName(userAgent: string): string | null {
  if (/KAKAOTALK/iu.test(userAgent)) return "카카오톡";
  if (/Instagram/iu.test(userAgent)) return "인스타그램";
  if (/FBAN|FBAV/iu.test(userAgent)) return "페이스북";
  if (/NAVER\(inapp|DaumApps/iu.test(userAgent)) return "포털 앱";
  if (/Line\//iu.test(userAgent)) return "라인";
  return null;
}

export type CameraOutcome = "ok" | "denied" | "unavailable";

/**
 * 카메라를 쓸 수 있는지 **먼저 물어본다**. 장면을 세운 뒤에 물으면 거부당했을 때
 * 검은 화면만 남고 화면이 이유를 말할 수 없다.
 */
export async function probeCamera(
  mediaDevices: { getUserMedia(constraints: MediaStreamConstraints): Promise<MediaStream> } | undefined,
): Promise<CameraOutcome> {
  if (!mediaDevices?.getUserMedia) return "unavailable";
  try {
    const stream = await mediaDevices.getUserMedia({
      video: { facingMode: { ideal: "environment" } },
      audio: false,
    });
    // 🚨 확인이 끝나면 바로 끈다. 안 끄면 렌즈가 잡힌 채로 남아 MindAR 이 카메라를 못 연다.
    for (const track of stream.getTracks()) track.stop();
    return "ok";
  } catch (error) {
    return (error as { name?: string })?.name === "NotAllowedError" ? "denied" : "unavailable";
  }
}
